import { useContext, useMemo } from "react";
import { OrderTypes } from "@shared/features/trades/common/types";
import { getConfigurationItem } from "@shared/features/bots/utils";
import { ToggleField } from "@shared/features/bots/BotForm/common/types";
import {
  BotType,
  LanguageNameSpaces,
  NumberOption,
  OverrideFieldType,
} from "@shared/types";
import { getItemOptions } from "@shared/features/configuration/utils";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import { isSymbol } from "@shared/types/utils";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

export interface UseTrailingAndTakeProfitProps {
  orderType: OrderTypes.TAKE_PROFIT | OrderTypes.ENTRY;
}

const useTrailingAndTakeProfit = ({
  orderType,
}: UseTrailingAndTakeProfitProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const {
    botGeneralData,
    trailingEntry,
    setTrailingEntry,
    trailingTakeProfit,
    setTrailingTakeProfit,
    trailingTakeProfitLastTargetOnly,
    setTrailingTakeProfitLastTargetOnly,
    botType,
    isMarginExchange,
  } = useContext(BotFormContext)!;
  const isGridBot = botType === BotType.GRID;
  const isSignalsBot = botType === BotType.SIGNAL;

  const rawValue =
    orderType === OrderTypes.ENTRY ? trailingEntry : trailingTakeProfit;
  const value = isSymbol(rawValue) ? undefined : rawValue;
  const placeholder = isSymbol(rawValue) ? t("labels.multi") : undefined;
  const onChange =
    orderType === OrderTypes.ENTRY ? setTrailingEntry : setTrailingTakeProfit;
  const itemName =
    orderType === OrderTypes.ENTRY ? "Trailing Entry" : "Trailing Take-Profit";
  const item = getConfigurationItem(botGeneralData, itemName);
  const fieldType =
    orderType === OrderTypes.ENTRY
      ? ToggleField.TRAILING_ENTRY
      : ToggleField.TRAILING_TAKE_PROFIT;
  const overrideFieldType =
    orderType === OrderTypes.ENTRY
      ? OverrideFieldType.TRAILING_ENTRY
      : OverrideFieldType.TRAILING_TAKE_PROFIT;

  const options: NumberOption[] = useMemo(() => {
    return getItemOptions(item)
      ?.filter((option: NumberOption) => !!option.value)
      .map((option: NumberOption) => ({
        value: option.value,
        name: `${option.name}%`,
      }));
  }, [item]);

  const isLeveraged = isSignalsBot && isMarginExchange;
  const getTitle = () => {
    if (isLeveraged) return t("labels.leveraged_trailing");
    else if (isGridBot) {
      return orderType === OrderTypes.ENTRY
        ? t("labels.trailing_entry")
        : t("labels.trailing_take_profit");
    }
    return t("labels.trailing");
  };

  return {
    value,
    onChange,
    lastTargetOnly: !!trailingTakeProfitLastTargetOnly,
    onLastTargetOnlyChange: setTrailingTakeProfitLastTargetOnly,
    showLastTargetOnly: isSignalsBot && orderType === OrderTypes.TAKE_PROFIT,
    fieldType,
    overrideFieldType,
    options,
    itemName,
    placeholder,
    title: getTitle(),
    isLeveraged,
  };
};

export default useTrailingAndTakeProfit;
